import type {
  AgentMode,
  ConversationEventDetails,
  ConversationTurn,
  ConversationTurnEntry,
  UUID,
} from '@/index'
import { isDuplicateUserPromptEvent, type Command } from './Command'
import type { TmuxSession, UserHostDetails } from '@/infra/sse/shared/payloads'
import { createClaudeSessionRuntime, type ClaudeSessionRuntime } from './ClaudeSessionRuntime'
import { extractProjectName } from '@/common/utils/pathUtils'
import adjectivesData from '../data/session-adjectives.json'
import famousPeopleData from '../data/session-famous-people.json'

const SESSION_NAMES_STORAGE_KEY = 'claude-session-names'
const MAX_GENERATION_ATTEMPTS = 10

export interface ClaudeSession {
  sessionId: UUID
  userExternalId: UUID
  transcriptPath: string
  contextWorkDirectory: string
  tmuxSession: TmuxSession | null
  userHostDetails: UserHostDetails
  conversationTurns: Map<UUID, ConversationTurn>
  sessionName: string | null
  sessionBreadcrumb: string
  dangerouslySkipPermissions: boolean | undefined
  autoModeSupported: boolean | undefined
  currentAgentMode: AgentMode
  runtime: ClaudeSessionRuntime
  isTransient: boolean
}

interface SessionNameEntry {
  readonly sessionId: string
  readonly sessionName: string
  readonly assignedAt: string
}

export function getDisplayHostName(session: ClaudeSession): string {
  return session.userHostDetails.hostName
}

export function getDisplayProjectName(session: ClaudeSession): string {
  return extractProjectName(session.contextWorkDirectory)
}

export function getDisplaySessionName(session: ClaudeSession): string {
  return session.sessionName ?? getOrCreateClaudeSessionName(session.sessionId, session)
}

export interface SessionSeed {
  readonly claudeSessionId: UUID
  readonly userExternalId: UUID
  readonly transcriptPath: string
  readonly contextWorkDirectory: string
  readonly dangerouslySkipPermissions: boolean | undefined
  readonly autoModeSupported: boolean | undefined
  readonly agentMode: AgentMode
  readonly tmuxSession: TmuxSession | null
  readonly hostDetails: UserHostDetails
  readonly isTransient?: boolean
}

export function createSession(seed: SessionSeed): ClaudeSession {
  const partialSession: ClaudeSession = {
    sessionId: seed.claudeSessionId,
    userExternalId: seed.userExternalId,
    transcriptPath: seed.transcriptPath,
    contextWorkDirectory: seed.contextWorkDirectory,
    tmuxSession: seed.tmuxSession,
    userHostDetails: seed.hostDetails,
    conversationTurns: new Map(),
    sessionName: null,
    sessionBreadcrumb: '',
    dangerouslySkipPermissions: seed.dangerouslySkipPermissions,
    autoModeSupported: seed.autoModeSupported,
    currentAgentMode: seed.agentMode,
    runtime: createClaudeSessionRuntime(),
    isTransient: seed.isTransient ?? false,
  }

  const sessionName = seed.tmuxSession?.sessionName
    ? formatTmuxSessionName(seed.tmuxSession.sessionName)
    : getOrCreateClaudeSessionName(seed.claudeSessionId, partialSession)

  const sessionBreadcrumb = buildSessionBreadcrumb(
    sessionName,
    seed.hostDetails.hostName,
    seed.contextWorkDirectory,
    seed.claudeSessionId,
  )

  return {
    ...partialSession,
    sessionName,
    sessionBreadcrumb,
  }
}

export function claudeSessionFrom(conversationEvent: ConversationEventDetails): ClaudeSession {
  const seed: SessionSeed = {
    claudeSessionId: conversationEvent.eventMetadata.claudeSessionId,
    userExternalId: conversationEvent.eventMetadata.userExternalId,
    transcriptPath: conversationEvent.eventMetadata.transcriptPath,
    contextWorkDirectory: conversationEvent.eventMetadata.contextWorkDirectory,
    dangerouslySkipPermissions: conversationEvent.eventMetadata.dangerouslySkipPermissions,
    autoModeSupported: conversationEvent.eventMetadata.autoModeSupported,
    agentMode: conversationEvent.agentMode,
    tmuxSession: conversationEvent.telemetry.tmuxSession || null,
    hostDetails: conversationEvent.userHostDetails,
  }

  return createSession(seed)
}

export function isInteractable(session: ClaudeSession): boolean {
  return session.tmuxSession !== null
}

export function isNotInteractable(session: ClaudeSession): boolean {
  return session.tmuxSession === null
}

export function hasAnyDrafts(session: ClaudeSession): boolean {
  if (session.runtime.memory.userInputDrafts.length > 0) {
    return true
  }

  const userAnswersDraft = session.runtime.memory.userAnswersDraft
  if (!userAnswersDraft) {
    return false
  }

  return Object.values(userAnswersDraft.answers).some(
    (answer) => answer.customAnswer !== null && answer.customAnswer.trim() !== '',
  )
}

export function getSessionEventsSortedNewestFirst(session: ClaudeSession): ConversationTurnEntry[] {
  const allEvents: ConversationTurnEntry[] = []

  for (const turn of session.conversationTurns.values()) {
    allEvents.push(...turn.events)
  }

  allEvents.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

  return allEvents
}

export function getLatestConversationTurn(session: ClaudeSession): ConversationTurn | null {
  if (session.conversationTurns.size === 0) return null

  const turns = Array.from(session.conversationTurns.values())
  turns.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())

  return turns[0] || null
}

export function getLatestDisplayableEvent(session: ClaudeSession): ConversationTurnEntry | null {
  const sortedEvents = getSessionEventsSortedNewestFirst(session)
  const allCommands = getAllCommands(session)

  for (const event of sortedEvents) {
    if (isDuplicateUserPromptEvent(event, allCommands)) {
      continue
    }
    return event
  }

  return null
}

export function formatTmuxSessionName(sessionName: string): string {
  return sessionName
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export function buildSessionBreadcrumb(
  sessionName: string | null,
  hostName: string,
  contextWorkDirectory: string,
  claudeSessionId: UUID,
): string {
  const parts: string[] = []

  if (hostName) {
    parts.push(hostName)
  }

  parts.push(formatContextName(contextWorkDirectory))

  const displaySessionName = sessionName || `Session ${claudeSessionId.substring(0, 8)}`
  parts.push(displaySessionName)

  return parts.length > 0 ? parts.join(' > ') : 'Unknown Session'
}

export function getOrCreateClaudeSessionName(sessionId: string, session: ClaudeSession): string {
  const storedSessionNames = getStoredSessionNames()

  if (storedSessionNames[sessionId]) {
    const storedName = storedSessionNames[sessionId].sessionName
    const currentTmuxName = session.tmuxSession?.sessionName?.trim()
      ? formatExistingSessionName(session.tmuxSession.sessionName)
      : null

    if (currentTmuxName && currentTmuxName !== storedName) {
      storeSessionName(sessionId, currentTmuxName)
      return currentTmuxName
    }

    return storedName
  }

  const existingSessionNames = Object.values(storedSessionNames).map(
    (assignment) => assignment.sessionName,
  )
  const newSessionName = session.tmuxSession?.sessionName
    ? formatExistingSessionName(session.tmuxSession.sessionName)
    : generateRandomSessionName(existingSessionNames)
  storeSessionName(sessionId, newSessionName)
  return newSessionName
}

export function getSessionLatestTimestamp(session: ClaudeSession): number {
  let latest = 0

  for (const turn of session.conversationTurns.values()) {
    if (turn.startTime) {
      const turnTime = new Date(turn.startTime).getTime()
      if (turnTime > latest) {
        latest = turnTime
      }
    }

    for (const event of turn.events) {
      if (event.timestamp) {
        const eventTime = new Date(event.timestamp).getTime()
        if (eventTime > latest) {
          latest = eventTime
        }
      }
    }
  }

  return latest
}

export function findEventByRefId(
  session: ClaudeSession,
  refEventId: string,
): ConversationTurnEntry | null {
  for (const turn of session.conversationTurns.values()) {
    const event = turn.events.find((e) => e.eventId === refEventId)
    if (event) return event
  }
  return null
}

function getAllCommands(session: ClaudeSession): Command[] {
  const commands: Command[] = []
  for (const turn of session.conversationTurns.values()) {
    commands.push(...turn.userCommands)
  }
  return commands
}

function formatContextName(contextPath: string): string {
  const contextName = contextPath.split('/').pop() || contextPath
  return contextName.replaceAll(/[-_]/g, ' ').replaceAll(/\b\w/g, (l) => l.toUpperCase())
}

function getStoredSessionNames(): Record<string, SessionNameEntry> {
  try {
    const storedSessionName = localStorage.getItem(SESSION_NAMES_STORAGE_KEY)
    return storedSessionName ? JSON.parse(storedSessionName) : {}
  } catch {
    return {}
  }
}

function formatExistingSessionName(sessionName: string): string {
  return formatTmuxSessionName(sessionName)
}

function storeSessionName(sessionId: string, sessionName: string): void {
  try {
    const storedSessionName = getStoredSessionNames()
    storedSessionName[sessionId] = {
      sessionId,
      sessionName,
      assignedAt: new Date().toISOString(),
    }
    localStorage.setItem(SESSION_NAMES_STORAGE_KEY, JSON.stringify(storedSessionName))
  } catch {
    /* ignored */
  }
}

function generateRandomSessionName(excludeNames: string[] = []): string {
  const adjectives: readonly string[] = adjectivesData
  const famousPeople: readonly string[] = famousPeopleData

  let attempts = 0

  while (attempts < MAX_GENERATION_ATTEMPTS) {
    const adjective = pickRandom(adjectives)
    const person = pickRandom(famousPeople)
    const generatedName = `${titleCase(adjective)} ${titleCase(person)}`

    if (!excludeNames.includes(generatedName)) {
      return generatedName
    }

    attempts++
  }

  return `Session ${Date.now().toString().slice(-6)}`
}

function pickRandom<T>(items: readonly T[]): T {
  if (items.length === 0) {
    throw new Error('Cannot pick from empty array')
  }
  const array = new Uint32Array(1)
  crypto.getRandomValues(array)
  const randomValue = array[0]
  if (randomValue === undefined) {
    throw new Error('Failed to generate random value')
  }
  const index = randomValue % items.length
  return items[index] as T
}

function titleCase(str: string): string {
  if (str.length === 0) return str
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}
